import React from "react";
import type { Notification } from "../features/notifications/interfaces/notifications.interface";
import { getActorName, getActorImage } from "../features/notifications/utils/notificationActor.utils";

export type NotificationItemVariant = "full" | "compact";

export interface NotificationItemProps {
  notification: Notification;
  variant?: NotificationItemVariant;
  /** Default avatar URL when actor image is missing (message type). */
  defaultAvatar: string;
  /** Logo icon URL for product/app notifications when no actor image. */
  logoIcon: string;
  /** Icon overlay for message type. */
  messageIcon: string;
  /** Icon overlay for app/product type. */
  appIcon: string;
  onClick?: () => void;
  onMarkRead?: () => void;
  onViewMessage?: () => void;
  /** Show three-dot action menu (full variant only). */
  showActionMenu?: boolean;
  /** Render divider after this item. */
  showDividerAfter?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification: notif,
  variant = "full",
  defaultAvatar,
  logoIcon,
  messageIcon,
  appIcon,
  onClick,
  onMarkRead,
  onViewMessage,
  showActionMenu = false,
  showDividerAfter = false,
}) => {
  const isMessage = notif.type === "message" || notif.type === "NEW_MESSAGE";
  const actorImage = getActorImage(notif);
  const actorName = getActorName(notif);

  const isReaction =
    (notif.meta as any)?.messageType === "REACTION" ||
    (notif.meta as any)?.type === "reaction" ||
    Boolean((notif.meta as any)?.reaction);
  const reactionValue = (notif.meta as any)?.reaction;

  const avatarSize = variant === "full" ? "w-14 h-14" : "w-12 h-12";
  const iconOverlaySize = variant === "full" ? "w-6 h-6" : "w-4 h-4";
  const iconInnerSize = variant === "full" ? "w-4 h-4" : "w-3 h-3";
  const logoImgClass = variant === "full" ? "w-10 h-10" : "w-7 h-7";
  const paddingX = variant === "full" ? "px-8" : "px-6";
  const paddingY = variant === "full" ? "py-4" : "py-3";
  const textSize = variant === "full" ? "15px" : "14px";
  const linkSize = variant === "full" ? "14px" : "13px";

  const avatarSrc =
    isMessage ? (actorImage || defaultAvatar) : notif.type === "product" && actorImage ? actorImage : null;

  return (
    <>
      <div
        className="transition-colors cursor-pointer"
        style={{ backgroundColor: notif.isRead ? "transparent" : "#F5FBFF" }}
        onClick={onClick}
      >
        <div className={`flex items-start space-x-4 ${paddingY} ${paddingX}`}>
          <div className="relative flex-shrink-0">
            <div
              className={`${avatarSize} rounded-full flex items-center justify-center overflow-hidden`}
              style={{
                backgroundColor: isMessage ? "#E3F2FD" : "#F9A825",
                border: "2px solid white",
              }}
            >
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={isMessage ? "Avatar" : "Seller"}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = isMessage ? defaultAvatar : logoIcon;
                  }}
                />
              ) : (
                <img
                  src={logoIcon}
                  alt="Logo"
                  className={logoImgClass}
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              )}
            </div>
            <div
              className={`absolute -bottom-1 -right-1 ${iconOverlaySize} rounded-full flex items-center justify-center`}
              style={{ backgroundColor: "#FFF" }}
            >
              <img
                src={isMessage ? messageIcon : appIcon}
                alt="Icon"
                className={iconInnerSize}
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p style={{ fontSize: textSize, color: notif.isRead ? "#939393" : "#616161" }}>
                  {isMessage ? (
                    isReaction && reactionValue ? (
                      <>
                        <span className="font-semibold">{actorName}</span>
                        <span> reacted &quot;{reactionValue}&quot; to a message</span>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold">{actorName}</span>
                        <span> {notif.body || "sent you a message"}</span>
                      </>
                    )
                  ) : (
                    <>
                      <span className="font-semibold">
                        {(notif as any).text ?? (notif.meta as any)?.text ?? (notif.title || actorName)}
                      </span>
                      {((notif as any).additionalText ?? (notif.meta as any)?.additionalText ?? notif.body) && (
                        <span> {(notif as any).additionalText ?? (notif.meta as any)?.additionalText ?? notif.body}</span>
                      )}
                    </>
                  )}
                </p>
                {!isMessage && ((notif as any).subText ?? (notif.meta as any)?.subText) && (
                  <p className="mt-1.5" style={{ color: "#9E9E9E", fontSize: linkSize }}>
                    {(notif as any).subText ?? (notif.meta as any)?.subText}
                  </p>
                )}

                {isMessage && !isReaction && (
                  <p
                    className="mt-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                    style={{
                      color: !notif.isRead ? "#64B5F6" : "#9E9E9E",
                      fontSize: linkSize,
                      textDecoration: "underline",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewMessage?.();
                    }}
                  >
                    Click to View
                  </p>
                )}
              </div>

              <div className="flex flex-col items-end ml-4 flex-shrink-0" style={{ gap: variant === "full" ? "4px" : "4px" }}>
                {showActionMenu && (
                  <div className="relative">
                    <button
                      type="button"
                      className="hover:opacity-70 p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkRead?.();
                      }}
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" style={{ color: "#4D4D4D" }}>
                        <circle cx="6" cy="12" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="18" cy="12" r="1.5" />
                      </svg>
                    </button>
                  </div>
                )}
                {notif.time && (
                  <span style={{ color: "#9E9E9E", fontSize: "12px" }}>{notif.time}</span>
                )}
                {!notif.isRead && (
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#64B5F6" }} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDividerAfter && <div className="border-b border-gray-100" />}
    </>
  );
};

export default NotificationItem;
